import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Snowflake, Clock, Leaf, ArrowRight } from 'lucide-react';
import UnifiedContactModal from './UnifiedContactModal';

const Hydrocoolers = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const features = [
    {
      icon: Snowflake,
      title: 'Rapid Cooling',
      description: 'Cool produce from field temperature to optimal storage temperature in minutes'
    },
    {
      icon: Clock,
      title: 'Extended Shelf Life',
      description: 'Increase produce shelf life by up to 300% with proper hydrocooling'
    },
    {
      icon: Leaf,
      title: 'Quality Preservation',
      description: 'Maintain freshness, texture, and nutritional value of harvested crops'
    }
  ];

  return (
    <section id="hydrocoolers" className={`${isMobile ? 'py-10' : 'py-20'} bg-gradient-to-br from-ctc-silver-light via-white to-ctc-silver-light`}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className={`text-center ${isMobile ? 'mb-8' : 'mb-16'}`}
        >
          <Link to="/hydrocoolers">
            <h2 className={`${isMobile ? 'text-3xl' : 'text-4xl lg:text-5xl'} font-bold text-gray-900 ${isMobile ? 'mb-3' : 'mb-6'} hover:text-ctc-blue transition-colors cursor-pointer`}>
              Hydrocoolers
            </h2>
          </Link>
          <p className={`${isMobile ? 'text-base' : 'text-xl'} text-gray-600 max-w-3xl mx-auto`}>
            Preserve crop quality and extend shelf life with our proven hydrocooling systems. 
            The crucial first step in your cold chain from harvest to market.
          </p>
        </motion.div>

        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center ${isMobile ? 'mb-8' : 'mb-16'}`}>
          {/* Features */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div>
              <h3 className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold text-gray-900 ${isMobile ? 'mb-2' : 'mb-4'}`}>
                Maximize Produce Value
              </h3>
              <p className={`text-gray-600 ${isMobile ? 'mb-3 text-sm' : 'mb-6'}`}>
                Our hydrocoolers rapidly remove field heat from freshly harvested produce, 
                significantly extending shelf life and maintaining premium quality.
              </p>
            </div>

            <div className={`${isMobile ? 'space-y-3' : 'space-y-6'}`}>
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="flex items-start space-x-4"
                >
                  <div className={`${isMobile ? 'w-10 h-10' : 'w-12 h-12'} bg-gradient-to-br from-ctc-blue-light to-ctc-blue rounded-lg flex items-center justify-center flex-shrink-0 shadow-md`}>
                    <feature.icon className="text-white" size={isMobile ? 20 : 24} />
                  </div>
                  <div>
                    <h4 className={`${isMobile ? 'text-base' : 'text-xl'} font-semibold text-gray-900 ${isMobile ? 'mb-1' : 'mb-2'}`}>
                      {feature.title}
                    </h4>
                    <p className={`text-gray-600 ${isMobile ? 'text-sm' : ''}`}>
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Product Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className={`bg-gradient-to-br from-ctc-silver-light via-white to-ctc-silver rounded-2xl ${isMobile ? 'p-4' : 'p-8'} shadow-xl`}>
              <img
                src="/Website copy.jpg"
                alt="CTC Waterworks hydrocooling systems in agricultural field setting"
                className={`w-full ${isMobile ? 'h-48' : 'h-80'} object-cover rounded-xl shadow-lg`}
              />
            </div>
            
            {/* Performance Badge */}
            <div className={`absolute -bottom-4 -left-4 bg-white rounded-xl shadow-xl ${isMobile ? 'p-2' : 'p-4'} border border-ctc-silver`}>
              <div className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-ctc-blue`}>300%</div>
              <div className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>Shelf Life Increase</div>
            </div>
          </motion.div>
        </div>

        {/* Applications */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className={`bg-white rounded-2xl ${isMobile ? 'p-4' : 'p-8'} ${isMobile ? 'mb-8' : 'mb-12'} shadow-lg border border-ctc-silver`}
        >
          <h3 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-gray-900 ${isMobile ? 'mb-3' : 'mb-6'} text-center`}>
            Perfect for These Crops
          </h3>
          <div className={`grid grid-cols-2 md:grid-cols-4 ${isMobile ? 'gap-2' : 'gap-6'}`}>
            {[
              { name: 'Cherries', icon: '🍒' },
              { name: 'Berries', icon: '🫐' },
              { name: 'Leafy Greens', icon: '🥬' },
              { name: 'Root Crops', icon: '🥕' },
              { name: 'Broccoli', icon: '🥦' },
              { name: 'Asparagus', icon: '🌱' },
              { name: 'Sweet Corn', icon: '🌽' },
              { name: 'Herbs', icon: '🌿' }
            ].map((crop, index) => (
              <div key={index} className={`text-center ${isMobile ? 'p-2' : 'p-4'} bg-gradient-to-br from-ctc-blue/5 to-ctc-blue/10 rounded-lg border border-ctc-blue/20`}>
                {crop.name === 'Cherries' ? (
                  <Link to="/cherry-hydrocoolers">
                    <div className={`${isMobile ? 'text-2xl mb-1' : 'text-3xl mb-2'}`}>{crop.icon}</div> 
                    <div className={`font-medium text-gray-900 ${isMobile ? 'text-sm' : ''}`}>{crop.name}</div> 
                  </Link>
                ) : (
                  <>
                    <div className={`${isMobile ? 'text-2xl mb-1' : 'text-3xl mb-2'}`}>{crop.icon}</div> 
                    <div className={`font-medium text-gray-900 ${isMobile ? 'text-sm' : ''}`}>{crop.name}</div> 
                  </>
                )}
              </div>
            ))}
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className={`text-center bg-gradient-to-r from-ctc-blue via-ctc-blue-dark to-black rounded-2xl ${isMobile ? 'p-6' : 'p-12'} text-white shadow-2xl`}
        >
          <h3 className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold ${isMobile ? 'mb-2' : 'mb-4'}`}>
            Preserve Your Harvest Value
          </h3>
          <p className={`${isMobile ? 'text-base' : 'text-xl'} ${isMobile ? 'mb-4' : 'mb-8'} opacity-90`}>
            Don't lose money to spoilage. Invest in professional hydrocooling.
          </p>
          <motion.button
            onClick={() => setIsContactModalOpen(true)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-white text-ctc-blue px-8 py-4 rounded-lg font-semibold hover:bg-ctc-silver-light transition-colors inline-flex items-center space-x-2 shadow-lg"
          >
            <span>Request Hydrocooler Quote</span>
            <ArrowRight size={20} />
          </motion.button>
        </motion.div>
      </div>

      {/* Unified Contact Modal */}
      <UnifiedContactModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)}
        productType="hydrocoolers"
        formSource="product_quote"
      />
    </section>
  );
};

export default Hydrocoolers;